import { datadogRum } from '@datadog/browser-rum';
import '@intigral/shared/styles/common.sass';
import { Partytown } from '@qwik.dev/partytown/react';
import mixpanel from 'mixpanel-browser';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import DynamicCSS from './components/DynamicCSS';
import ErrorBoundary from './components/ErrorBoundry';
import { appConfig } from './config/app.config';
import KochavaContextProvider from './contexts/KochavaContext';
import MixpanelContextProvider from './contexts/MixpanelContext';
import h from './helpers/common';
import { IStore } from './interfaces/StoreTypes';
import { MovieDetailsOverlayPage } from './features/detail-overlay';
import { PPVDetailModal } from './features/ppv';
import UpgradedConfirmModal from '../src/components/elements/UpgradeConfirmModal';
import GuestRedirectConfirmModal from './components/elements/GuestRedirectConfirmModal';
import i18n from './translations';
import { showGuestRedirectModal, showUpgradedModal } from './store/reducers/generalConfigurationSlice';
import GamePopup from './components/elements/GamePopup';
import useCdnBalancer from './hooks/useCdnBalancer';
import { useConfiguration } from './hooks/useConfiguration';
import { useUpgradedModalState, useGuestRedirectModalState } from './hooks/usePPVEvents';
import { PaymentFailurePage } from './screen/PaymentFailurePage';
import CatchUpDetailModal from './components/elements/catchup/CatchUpDetailModal';
import { getPathNameWithoutLangPrefix, applyMask, MaskMode } from '@intigral/utils';
import user from './helpers/user';
import { ROUTE_CONSTANT } from '@libs/constants';
import GamificationWelcomeModal from './pages/GamificationWelcomeModal';
import { isGamificationEnabled } from './features/gamification/utils/GamificationUtils';
import { container } from '@intigral/domain';
import { ISubscriptionService } from '@intigral/domain/lib/application/services/SubscriptionService';
import { DI } from '@intigral/constant/config/DIContainer';
import usePPVInfo from './hooks/usePPVInfo';
import { formatMixpanelKeys } from './utils/formatKeys';
import { useLogout } from './features/header/hooks/useLogout';
import { KEYS } from './contexts/MixpanelConstants';
import { isGuestUser, getFreeTrailCtaUrl, redirectGuestToSignIn } from './config/guestMode';
import { mixpanelTrackingEvent } from './utils/mixpanelTrackingEventHandler';
import { setProfileSwitchNavigate } from './utils/profileSwitchRedirect';
import { dataDogBeforeSend, renewDataDogSessionIfNeeded } from '@intigral/utils/lib/datadog';

interface IProps {
    context: any;
}

export const LogoutEventListener = () => {
    const { logoutUser } = useLogout();

    useEffect(() => {
        const handleForceLogout = () => {
            logoutUser();
        };

        window.addEventListener('force-logout', handleForceLogout);

        return () => {
            window.removeEventListener('force-logout', handleForceLogout);
        };
    }, [logoutUser]);

    return null;
};

const App = ({ context }: IProps) => {
    h.setContext(context);
    user.setUserActiveSubscribedGuids();
    const userObject = useSelector((state: IStore) => state?.user);
    const location = useLocation();
    const navigate = useNavigate();
    const { setPpvSubscribedList } = usePPVInfo();

    const params = useParams();
    const language = params['lang'] === 'en' ? 'en' : 'ar';
    document.body.setAttribute('dir', h.getPageDirection(language));
    i18n.changeLanguage(language);

    useEffect(() => {
        setProfileSwitchNavigate(navigate);
    }, [navigate]);
    //  Using React Query instead of Redux for configuration
    const { data: config = {} } = useConfiguration();
    const gamificationConfig = isGamificationEnabled();

    //  FIXED: Using React Query-style hook instead of Redux generalConfiguration
    const { showUpgradedModal: showUpgradedModalSelector } = useUpgradedModalState();
    const { showGuestRedirectModal: showGuestRedirectModalSelector } = useGuestRedirectModalState();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);

    const handleGuestRedirectFreeTrialClick = () => {
        dispatch(showGuestRedirectModal(false));
        const href = getFreeTrailCtaUrl();
        if (href) {
            window.location.assign(href);
        }
    };

    const cdnBalancer = useCdnBalancer();
    const getSubscriptionStatus = async () => {
        try {
            setLoading(true);
            const userInfo = user.getUserInfo();
            // Guests already have tier/subscription from login defaults.
            if (isGuestUser(userInfo)) {
                setPpvSubscribedList?.(userInfo.ppvList ?? []);
                setLoading(false);
                return;
            }

            const subscriptionService = container.resolve<ISubscriptionService>(DI.SERVICES.ISubscriptionService);
            const subscription = await subscriptionService.getSubscriptionStatus(
                user.getAPIKey(),
                userInfo?.username,
                userInfo?.authToken,
                userInfo?.identityToken
            );
            if (subscription) {
                setLoading(false);
                delete subscription['identityToken'];
                user.updateUserSubscriptionsObject(subscription, false);
                const { ppvList } = subscription;
                if (setPpvSubscribedList) {
                    setPpvSubscribedList(ppvList);
                }
            }
        } catch (error) {
            setLoading(false);
        }
    };

    useEffect(() => {
        getSubscriptionStatus();
        if (
            !datadogRum.getInitConfiguration() &&
            appConfig.dataDogRUM.isEnabled &&
            appConfig.dataDogRUM.clientToken != '' &&
            appConfig.dataDogRUM.appId != ''
        ) {
            try {
                const rum = appConfig.dataDogRUM;
                datadogRum.init({
                    // remoteConfiguration: rum.remoteConfigurationId
                    //     ? { id: rum.remoteConfigurationId }
                    //     : undefined,
                    applicationId: rum.appId,
                    clientToken: rum.clientToken,
                    site: rum.site,
                    service: rum.service,
                    env: rum.env,
                    version: rum.version,
                    sessionSampleRate: Number(rum.sessionSampleRate || 100),
                    sessionReplaySampleRate: Number(rum.sessionReplaySampleRate || 100),
                    traceSampleRate: Number(rum.traceSampleRate || 100),
                    profilingSampleRate: Number(rum.profilingSampleRate || 100),
                    trackUserInteractions: rum.trackUserInteractions === true || rum.trackUserInteractions === 'true',
                    trackResources: rum.trackResources === true || rum.trackResources === 'true',
                    trackLongTasks: rum.trackLongTasks === true || rum.trackLongTasks === 'true',
                    trackSessionAcrossSubdomains: rum.trackSessionAcrossSubdomains,
                    defaultPrivacyLevel: rum.defaultPrivacyLevel || 'mask-user-input',
                    enablePrivacyForActionName: rum.enablePrivacyForActionName,
                    trackAnonymousUser: rum.trackAnonymousUser,
                    actionNameAttribute: rum.actionNameAttribute,
                    traceContextInjection: rum.traceContextInjection,
                    allowedTracingUrls: rum.allowedTracingUrls,
                    allowedTrackingOrigins: rum.allowedTrackingOrigins,
                    trackingConsent: 'granted',
                    beforeSend: (event, context) => dataDogBeforeSend(event as never, context as never),
                });
                // datadogRum.startSessionReplayRecording({ force: true });
                renewDataDogSessionIfNeeded();
            } catch (error) {
                console.error('[Datadog RUM] Initialization failed:', error);
            }
        }

        /**
         * Initialize CDN Balancer if enabled (logic is internally handled in the hook)
         * default value for bucketName is VOD which includes (Trailers, Auto play trailers, VODs and TVShows)
         * will change when changing the player (VOD or LIVE)
         */
        if (cdnBalancer.isConfigReady && cdnBalancer.initBalancer) {
            cdnBalancer.initBalancer();
        }
    }, [cdnBalancer.isConfigReady]);

    useEffect(() => {
        if (!appConfig.dataDogRUM.isEnabled) {
            return;
        }

        const userInfo = userObject ?? user.getUserInfo();
        const guest = isGuestUser(userInfo);
        const uid = user.getUserInfo()?.uid ?? context?.user?.uid;

        if (guest) {
            datadogRum.clearUser();
            return;
        }

        if (!uid && !userInfo?.username) {
            datadogRum.clearUser();
            return;
        }

        datadogRum.setUser({
            id: String(uid || ''),
            name: applyMask(userInfo?.username ?? '', MaskMode.Partial),
        });
    }, [userObject?.username, userObject?.isGuest, context?.user?.uid]);

    useEffect(() => {
        if (appConfig.dataDogRUM.isEnabled) {
            datadogRum.setGlobalContextProperty('language', language);
        }
    }, [language]);

    mixpanel.init(h.getMixPanelKey(), {
        secure_cookie: true,
        loaded: function (mixpanel) {
            mixpanel.identify(context?.user?.uid || context.user.accountId);
            mixpanel.people.unset(KEYS.PEOPLE.PHONE);
            mixpanelTrackingEvent.setMixpanel(mixpanel);
        },
        persistence: 'localStorage',
    });

    mixpanel.set_config({ persistence: 'localStorage' });
    const superProperties = (config as any)?.analytics?.mixpanel?.init_super_properties || {};
    const formattedKeys = formatMixpanelKeys(superProperties);
    mixpanel.register(formattedKeys);
    mixpanel.register({ [KEYS.SUPER.GUEST_USER]: isGuestUser(context?.user ?? user.getUserInfo()) });

    const showPaymentFailurePopup = () => {
        const pathWithoutLang = getPathNameWithoutLangPrefix(location?.pathname);
        if (
            [
                ROUTE_CONSTANT.CONSENT_MANAGEMENT?.toLowerCase(),
                ROUTE_CONSTANT.SELECT_PROFILE?.toLowerCase(),
                ROUTE_CONSTANT.COLD_START?.toLowerCase(),
            ].includes(pathWithoutLang?.toLowerCase())
        ) {
            return false;
        }
        return true;
    };

    return (
        <DynamicCSS>
            <ErrorBoundary>
                <MixpanelContextProvider analytic={mixpanel} user={userObject}>
                    <KochavaContextProvider>
                        <Outlet />
                        <LogoutEventListener />
                        <CatchUpDetailModal />
                        <MovieDetailsOverlayPage />
                        {!loading && <PPVDetailModal />}
                        {gamificationConfig?.enable && <GamificationWelcomeModal />}
                        <GamePopup />
                        <UpgradedConfirmModal
                            isOpen={showUpgradedModalSelector}
                            handleCloseModal={() => dispatch(showUpgradedModal(false))}
                        />
                        <GuestRedirectConfirmModal
                            isOpen={showGuestRedirectModalSelector}
                            handleCloseModal={() => dispatch(showGuestRedirectModal(false))}
                            onFreeTrialClick={handleGuestRedirectFreeTrialClick}
                            onSignInClick={redirectGuestToSignIn}
                        />
                        {showPaymentFailurePopup() ? <PaymentFailurePage /> : <></>}
                        <Partytown
                            debug={process.env.NODE_ENV === 'development'}
                            loadScriptsOnMainThread={['https://www.googletagmanager.com/gtm.js']}
                            forward={[['dataLayer.push', { preserveBehavior: true }]]}
                        />
                    </KochavaContextProvider>
                </MixpanelContextProvider>
            </ErrorBoundary>
        </DynamicCSS>
    );
};

export default App;
